<template>
  <section
    class="try-source-download try-screen"
    :class="{ 'try-source-download--compact': compact }"
    role="status"
    aria-live="polite"
  >
    <div class="tsd-stage">
      <div class="tsd-node tsd-node--clipr">
        <span class="tsd-node-halo tsd-node-halo--clipr" aria-hidden="true"></span>
        <span class="tsd-node-icon tsd-node-icon--clipr">
          <img src="/favicon.svg" alt="Clipr" />
        </span>
        <span class="tsd-node-label">Clipr</span>
      </div>

      <div class="tsd-conduit">
        <span class="tsd-arrow-track" aria-hidden="true"></span>
        <span class="tsd-arrow-track-glow" aria-hidden="true"></span>
        <span class="tsd-arrow-flow tsd-arrow-flow--primary" aria-hidden="true"></span>
        <span class="tsd-arrow-flow tsd-arrow-flow--secondary" aria-hidden="true"></span>
        <span class="tsd-arrow-head" aria-hidden="true"><i class="ti-arrow-left"></i></span>

        <div class="tsd-thumb">
          <img v-if="thumbnail" :src="thumbnail" :alt="resolvedTitle" class="tsd-thumb-img" />
          <span v-else class="tsd-thumb-fallback"><i class="ti-control-play"></i></span>
          <span class="tsd-thumb-scan" aria-hidden="true"></span>
          <span class="tsd-thumb-vignette" aria-hidden="true"></span>
        </div>
      </div>

      <div class="tsd-node tsd-node--platform">
        <span class="tsd-node-halo tsd-node-halo--platform" aria-hidden="true"></span>
        <span class="tsd-node-icon tsd-node-icon--platform">
          <img v-if="platformIconUrl" :src="platformIconUrl" :alt="platformLabel" />
          <i v-else class="ti-world"></i>
        </span>
        <span class="tsd-node-label">{{ platformLabel || $t("tryClips.sourceDownload.sourceFallback") }}</span>
      </div>
    </div>

    <template v-if="compact">
      <p v-if="eyebrow" class="tsd-eyebrow">{{ eyebrow }}</p>
      <p v-if="showSize" class="tsd-size">{{ $t("tryClips.sourceDownload.downloadSize", { size: formattedSize }) }}</p>
      <div v-if="showEtaTimer" class="tsd-eta-slot">
        <try-eta-timer :message="hint" />
      </div>
      <p v-else-if="showHint" class="tsd-subtitle tsd-hint">{{ hint }}</p>
    </template>
    <template v-else>
      <p class="tsd-eyebrow">{{ $t("tryClips.sourceDownload.eyebrow", { platform: platformLabel }) }}</p>
      <h3 class="tsd-title">{{ resolvedTitle }}</h3>
      <p class="tsd-subtitle">{{ $t("tryClips.sourceDownload.subtitle") }}</p>
      <p v-if="showSize" class="tsd-size">{{ $t("tryClips.sourceDownload.downloadSize", { size: formattedSize }) }}</p>
    </template>
  </section>
</template>

<script>
import TryEtaTimer, { isTryEtaHiddenMessage, isTryEtaMessage } from "@/components/try/TryEtaTimer.vue";

export default {
  name: "TrySourceDownload",
  components: {
    TryEtaTimer,
  },
  props: {
    thumbnail: {
      type: String,
      default: "",
    },
    title: {
      type: String,
      default: "",
    },
    platformIconUrl: {
      type: String,
      default: "",
    },
    platformLabel: {
      type: String,
      default: "",
    },
    compact: {
      type: Boolean,
      default: false,
    },
    eyebrow: {
      type: String,
      default: "",
    },
    hint: {
      type: String,
      default: "",
    },
    sizeBytes: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    resolvedTitle() {
      return this.title || this.$t("tryClips.sourceDownload.genericTitle");
    },
    showEtaTimer() {
      return isTryEtaMessage(this.hint);
    },
    showHint() {
      const hint = String(this.hint || "").trim();
      if (!hint || isTryEtaHiddenMessage(hint)) return false;
      return !isTryEtaMessage(hint);
    },
    formattedSize() {
      const bytes = Number(this.sizeBytes) || 0;
      if (bytes <= 0) return "";
      const gb = bytes / 1024 ** 3;
      if (gb >= 1) return `${gb.toFixed(1)} GB`;
      const mb = bytes / 1024 ** 2;
      return `${Math.max(1, Math.round(mb))} MB`;
    },
    showSize() {
      return this.formattedSize !== "";
    },
  },
};
</script>

<style scoped>
.try-source-download {
  --tsd-node-icon-size: 64px;
  --tsd-conduit-height: 116px;
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  text-align: center;
}

.tsd-stage {
  position: relative;
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 14px;
  margin-bottom: 22px;
}

.tsd-node {
  position: relative;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 92px;
  z-index: 1;
}

.tsd-node-halo {
  position: absolute;
  top: calc((var(--tsd-node-icon-size) - 12px) / -2);
  left: 50%;
  width: calc(var(--tsd-node-icon-size) + 18px);
  height: calc(var(--tsd-node-icon-size) + 18px);
  transform: translateX(-50%);
  border-radius: 50%;
  pointer-events: none;
}

.tsd-node-halo--clipr {
  background: radial-gradient(circle, rgba(168, 85, 247, 0.22) 0%, transparent 68%);
  animation: tsd-halo-receive 2.4s ease-in-out infinite;
}

.tsd-node-halo--platform {
  background: radial-gradient(circle, rgba(34, 211, 238, 0.18) 0%, transparent 68%);
  animation: tsd-halo-send 2.4s ease-in-out infinite;
}

.tsd-node-icon {
  position: relative;
  width: var(--tsd-node-icon-size);
  height: var(--tsd-node-icon-size);
  border-radius: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(148, 163, 184, 0.28);
  background:
    radial-gradient(circle at 50% 35%, rgba(34, 211, 238, 0.16), transparent 64%),
    rgba(15, 23, 42, 0.72);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 12px 26px rgba(15, 23, 42, 0.32);
}

.tsd-node-icon img {
  width: 36px;
  height: 36px;
  object-fit: contain;
}

.tsd-node-icon i {
  font-size: 1.5rem;
  color: #c4b5fd;
}

.tsd-node-icon--clipr {
  border-color: rgba(196, 181, 253, 0.45);
  background:
    radial-gradient(circle at 50% 30%, rgba(168, 85, 247, 0.28), transparent 66%),
    rgba(15, 23, 42, 0.78);
  animation: tsd-clipr-pulse 1.8s ease-in-out infinite;
}

.tsd-node-icon--platform {
  border-color: rgba(34, 211, 238, 0.32);
  background:
    radial-gradient(circle at 50% 30%, rgba(34, 211, 238, 0.2), transparent 66%),
    rgba(15, 23, 42, 0.78);
  animation: tsd-platform-pulse 2.2s ease-in-out infinite;
}

.tsd-node-label {
  color: var(--clipr-text-secondary, #cbd5e1);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.tsd-conduit {
  position: relative;
  flex: 1;
  min-width: 0;
  height: var(--tsd-conduit-height);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: calc((var(--tsd-node-icon-size) - var(--tsd-conduit-height)) / 2);
  z-index: 1;
}

.tsd-arrow-track {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  transform: translateY(-50%);
  background: repeating-linear-gradient(
    90deg,
    rgba(34, 211, 238, 0.55) 0,
    rgba(34, 211, 238, 0.55) 8px,
    transparent 8px,
    transparent 16px
  );
  opacity: 0.75;
  animation: tsd-track-shift 1.1s linear infinite;
}

.tsd-arrow-track-glow {
  position: absolute;
  top: 50%;
  left: 4%;
  right: 4%;
  height: 10px;
  transform: translateY(-50%);
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(168, 85, 247, 0.08), rgba(34, 211, 238, 0.22), rgba(168, 85, 247, 0.08));
  filter: blur(6px);
  opacity: 0.85;
}

.tsd-arrow-flow {
  position: absolute;
  top: 50%;
  left: 0;
  border-radius: 999px;
  transform: translate(-30%, -50%);
  filter: blur(0.5px);
}

.tsd-arrow-flow--primary {
  width: 52px;
  height: 10px;
  background: radial-gradient(ellipse at center, rgba(103, 232, 249, 0.98) 0%, rgba(34, 211, 238, 0) 72%);
  animation: tsd-flow 1.35s cubic-bezier(0.45, 0, 0.55, 1) infinite;
}

.tsd-arrow-flow--secondary {
  width: 28px;
  height: 6px;
  background: radial-gradient(ellipse at center, rgba(196, 181, 253, 0.9) 0%, rgba(168, 85, 247, 0) 72%);
  animation: tsd-flow 1.85s cubic-bezier(0.45, 0, 0.55, 1) 0.45s infinite;
}

.tsd-arrow-head {
  position: absolute;
  top: 50%;
  left: -4px;
  transform: translateY(-50%);
  color: #67e8f9;
  font-size: 1rem;
  line-height: 1;
  filter: drop-shadow(0 0 8px rgba(34, 211, 238, 0.55));
  animation: tsd-arrow-pulse 1.35s ease-in-out infinite;
}

.tsd-thumb {
  position: relative;
  z-index: 2;
  width: 188px;
  max-width: 56%;
  aspect-ratio: 16 / 9;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(196, 181, 253, 0.35);
  background: rgba(2, 6, 23, 0.92);
  box-shadow:
    0 18px 40px rgba(8, 12, 26, 0.55),
    0 0 24px rgba(34, 211, 238, 0.1);
  animation: tsd-thumb-bob 2.6s ease-in-out infinite;
}

.tsd-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.tsd-thumb-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #67e8f9;
  font-size: 1.6rem;
}

.tsd-thumb-scan {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(34, 211, 238, 0.12) 46%,
    rgba(103, 232, 249, 0.42) 50%,
    rgba(34, 211, 238, 0.12) 54%,
    transparent 100%
  );
  transform: translateY(-100%);
  animation: tsd-scan 1.8s ease-in-out infinite;
}

.tsd-thumb-vignette {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(2, 6, 23, 0.42) 0%, transparent 22%, transparent 78%, rgba(2, 6, 23, 0.48) 100%),
    linear-gradient(90deg, rgba(2, 6, 23, 0.28) 0%, transparent 18%, transparent 82%, rgba(2, 6, 23, 0.28) 100%);
  pointer-events: none;
}

.tsd-eyebrow {
  margin: 0 0 10px;
  color: #a5f3fc;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.tsd-title {
  margin: 0 auto 6px;
  max-width: 32ch;
  color: #f8fafc;
  font-size: clamp(1.15rem, 2.4vw, 1.5rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.tsd-subtitle {
  margin: 0;
  color: #94a3b8;
  font-size: 0.9rem;
}

.tsd-size {
  margin: 10px 0 0;
  color: #a5f3fc;
  font-size: 0.86rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-shadow: 0 0 18px rgba(34, 211, 238, 0.18);
}

@keyframes tsd-halo-receive {
  0%, 100% {
    opacity: 0.45;
    transform: translateX(-50%) scale(0.92);
  }
  50% {
    opacity: 1;
    transform: translateX(-50%) scale(1.08);
  }
}

@keyframes tsd-halo-send {
  0%, 100% {
    opacity: 0.55;
    transform: translateX(-50%) scale(1);
  }
  50% {
    opacity: 0.85;
    transform: translateX(-50%) scale(0.94);
  }
}

@keyframes tsd-track-shift {
  to {
    background-position: 16px 0;
  }
}

@keyframes tsd-flow {
  0% {
    left: 100%;
    opacity: 0;
  }
  12% {
    opacity: 1;
  }
  88% {
    opacity: 1;
  }
  100% {
    left: 0;
    opacity: 0;
  }
}

@keyframes tsd-arrow-pulse {
  0%, 100% {
    opacity: 0.75;
    transform: translateY(-50%) translateX(0);
  }
  50% {
    opacity: 1;
    transform: translateY(-50%) translateX(-2px);
  }
}

@keyframes tsd-thumb-bob {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

@keyframes tsd-scan {
  0% {
    transform: translateY(-100%);
  }
  60%, 100% {
    transform: translateY(100%);
  }
}

@keyframes tsd-clipr-pulse {
  0%, 100% {
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.06),
      0 12px 26px rgba(15, 23, 42, 0.32),
      0 0 0 0 rgba(168, 85, 247, 0);
  }
  50% {
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.06),
      0 12px 26px rgba(15, 23, 42, 0.32),
      0 0 0 10px rgba(168, 85, 247, 0.14);
  }
}

@keyframes tsd-platform-pulse {
  0%, 100% {
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.06),
      0 12px 26px rgba(15, 23, 42, 0.32),
      0 0 0 0 rgba(34, 211, 238, 0);
  }
  50% {
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.06),
      0 12px 26px rgba(15, 23, 42, 0.32),
      0 0 0 8px rgba(34, 211, 238, 0.1);
  }
}

.try-source-download--compact {
  --tsd-node-icon-size: 44px;
  --tsd-conduit-height: 72px;
  width: 100%;
  max-width: 460px;
  margin-left: auto;
  margin-right: auto;
  align-self: center;
}

.try-source-download--compact .tsd-stage {
  gap: 8px;
  margin-bottom: 14px;
}

.try-source-download--compact .tsd-node {
  width: 64px;
  gap: 5px;
}

.try-source-download--compact .tsd-node-icon {
  border-radius: 13px;
}

.try-source-download--compact .tsd-node-icon img {
  width: 24px;
  height: 24px;
}

.try-source-download--compact .tsd-node-icon i {
  font-size: 1.05rem;
}

.try-source-download--compact .tsd-node-label {
  font-size: 0.66rem;
}

.try-source-download--compact .tsd-thumb {
  width: 120px;
}

.try-source-download--compact .tsd-arrow-flow--primary {
  width: 32px;
}

.try-source-download--compact .tsd-arrow-flow--secondary {
  width: 22px;
}

.try-source-download--compact .tsd-hint {
  margin-top: 6px;
  font-size: 0.82rem;
}

.try-source-download--compact .tsd-size {
  margin-top: 6px;
  font-size: 0.78rem;
}

.tsd-eta-slot {
  width: 100%;
  display: flex;
  justify-content: center;
}

@media (max-width: 560px) {
  .try-source-download {
    --tsd-node-icon-size: 50px;
    --tsd-conduit-height: 96px;
  }

  .tsd-stage {
    gap: 6px;
  }

  .tsd-node {
    width: 64px;
  }

  .tsd-node-icon {
    border-radius: 14px;
  }

  .tsd-node-icon img {
    width: 28px;
    height: 28px;
  }

  .tsd-thumb {
    width: 140px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tsd-arrow-track,
  .tsd-arrow-flow,
  .tsd-arrow-head,
  .tsd-thumb,
  .tsd-thumb-scan,
  .tsd-node-halo,
  .tsd-node-icon--clipr,
  .tsd-node-icon--platform {
    animation: none;
  }

  .tsd-thumb-scan {
    display: none;
  }
}
</style>
